import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicLayout from "../../components/PublicLayout";
import NotFound from "./NotFound";
import { getEvent } from "../../content/eventsRegistry";
import { SITE } from "../../content/site";
import { routePath } from "../../lib/routes";
import { track, EVENTS } from "../../lib/analytics";

// Immersive template for individual Equestrian Partners events (driven by eventsRegistry).
export default function EventDetail({ lang }) {
  const { slug } = useParams();
  const ev = getEvent(slug);
  useEffect(() => { window.scrollTo(0, 0); if (ev) track(EVENTS.PAGE_VIEW, { page: `event_${slug}`, lang }); }, [lang, slug, ev]);
  if (!ev) return <NotFound />;
  const d = ev.content[lang] || ev.content.en;

  return (
    <PublicLayout pageKey="events" lang={lang} overlayNav
      crumbs={[{ name: SITE[lang].breadcrumbs.events, path: routePath("events", lang) }]}>
      <div data-testid="event-detail-page">
        <section className="relative h-[92vh] min-h-[620px] overflow-hidden bg-[#050505]" data-testid="event-hero">
          {ev.video ? (
            <video muted playsInline autoPlay loop preload="auto" poster={ev.img}
              className="absolute inset-0 h-full w-full object-cover">
              <source src={ev.video} type="video/mp4" />
            </video>
          ) : (
            <img src={ev.img} alt={d.title} className="absolute inset-0 h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/25" />
          <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 md:px-12 md:pb-24">
            <p className="overline text-white/70">{d.overline}</p>
            <h1 className="font-display mt-5 max-w-4xl text-4xl font-light leading-[1.02] tracking-tightest text-white sm:text-5xl lg:text-7xl">{d.title}</h1>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/80 md:text-lg">{d.sub}</p>
          </div>
        </section>

        {d.facts?.length > 0 && (
          <section className="mx-auto grid max-w-[1200px] gap-x-8 gap-y-8 px-6 py-16 sm:grid-cols-2 md:px-8 md:py-20 lg:grid-cols-4" data-testid="event-facts">
            {d.facts.map((f, i) => (
              <div key={i} className="border-t border-black/10 pt-4">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#9a9d98]">{f.k}</p>
                <p className="mt-2 text-base font-light text-[#050505]">{f.v}</p>
              </div>
            ))}
          </section>
        )}

        {(d.sections || []).map((s, i) =>
          s.type === "media" ? (
            <section key={i} className="relative min-h-[60vh] overflow-hidden md:min-h-[80vh]" data-testid={`event-section-${i}`}>
              <img src={s.img} alt={s.title || d.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
              <div className="relative mx-auto flex min-h-[60vh] max-w-[1200px] flex-col justify-end px-6 pb-14 md:min-h-[80vh] md:px-8 md:pb-20">
                {s.overline && <p className="overline mb-4 text-white/55">{s.overline}</p>}
                {s.title && <h2 className="font-display max-w-3xl text-2xl font-light tracking-tight text-white md:text-4xl">{s.title}</h2>}
              </div>
            </section>
          ) : (
            <section key={i} className="mx-auto max-w-[1200px] px-6 py-20 md:px-8 md:py-28" data-testid={`event-section-${i}`}>
              {s.overline && <p className="overline mb-4 text-[#9a9a9a]">{s.overline}</p>}
              {s.title && <h2 className="font-display max-w-3xl text-3xl font-light leading-tight tracking-tight text-[#050505] md:text-5xl">{s.title}</h2>}
              {s.text && <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4a4a4a] md:text-lg">{s.text}</p>}
            </section>
          )
        )}

        <section className="mx-auto max-w-[1200px] px-6 pb-28 text-center md:px-8">
          <Link to={routePath("contact", lang)} data-testid="event-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#050505] px-9 py-4 text-sm font-medium text-white transition-opacity hover:opacity-85">
            {d.cta} <ArrowRight size={16} />
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}
